import { CircularProgress, FormControlLabel, Switch } from "@mui/material";
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { SimpleForm, TextInput, SelectInput, required, DateInput, FileField, FileInput, useRedirect, TopToolbar, Button, Edit, useGetOne, useNotify, CheckboxGroupInput, Toolbar, SaveButton, DeleteButton, useSaveContext, useDelete } from "react-admin";
import VisibilityIcon from '@mui/icons-material/Visibility';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box } from "@mui/material";
import { Link, useLocation, useParams } from "react-router-dom";
import { set, useFormContext, useWatch } from "react-hook-form";
import { areaChoices, checkMonthChoices, checkWeekChoices, customerTypeChoices, extractCity, extractPrefecture, extractTown, formatPostalCode, getNameAndCompanyNamewithAddress, getRomaToKana, handleAddressChange, handleCityListLoad, handlePostCodeChange, handleTownListLoad, initKuroshiro, validateKatakana } from "../common";

const CustomerEdit = () => {
  const redirect = useRedirect();
  const notify = useNotify();
  const { id } = useParams();
  const [syncContractInfo, setSyncContractInfo] = useState(false); // 🔹 トグル状態
  const location = useLocation();
  const resource = location.pathname.split("/")[1];
  const { data, isLoading, refetch } = useGetOne("customers", { id });

  const [requesters, setRequesters] = useState([]);

  const [customerTypes, setCustomerTypes] = useState({
    isCleanCus: 0,
    isInspectCus: 0,
    isConstructCus: 0,
    isGarbageCommitCus: 0,
    isGarbageAllowCus: 0,
    isWasteCus: 0,
  });

  // カスタムツールバー：保存ボタンを常に有効にする
  const AlwaysEnabledToolbar = (props) => {
    // カスタム保存ボタン（常に有効）
    const CustomSaveButton = () => {
      const { save } = useSaveContext();
      const { handleSubmit } = useFormContext();
      
      const handleSave = () => {
        handleSubmit((data) => {
          save(data);
        })();
      };
      
      return (
        <Button
          label="保存"
          onClick={handleSave}
          startIcon={<SaveIcon />}
          variant="contained"
          sx={{
            backgroundColor: '#1976d2',
            color: 'white',
            p: 1,
            pr: 2,
            pl: 2,
            '&:hover': {
              backgroundColor: '#115293',
            },
            mr: 1
          }}
        />
      );
    };

    // カスタム削除ボタン（useDeleteを使用）
    const CustomDeleteButton = () => {
      const [deleteRecord] = useDelete();
      
      const handleDelete = async () => {
        if (window.confirm('本当に削除しますか？')) {
          try {
            await deleteRecord('customers', { id, previousData: data });
            console.log('Delete successful');
            notify('削除が完了しました', { type: 'success' }); 
            redirect('list', resource); 
          } catch (error) {
            console.error('Delete failed:', error);
            notify('削除に失敗しました', { type: 'error' });
          }
        }
      };
      
      return (
        <Button
          label="削除"
          onClick={handleDelete}
          startIcon={<DeleteIcon />}
          variant="outlined"
          color="error"
          sx={{
            p: 1,
            pr: 2,
            pl: 2,
          }}
        />
      );
    };

    return (
      <Toolbar {...props} style={{ justifyContent: 'space-between' }}>
        <CustomSaveButton />
        <CustomDeleteButton />
      </Toolbar>
    );
  };

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_DIR}/requesters/all`, {
          method: "GET",
          headers: new Headers({ "Content-Type": "application/json" }),
          mode: "cors",
          credentials: "include",
        });
        if (!response.ok) {
          throw new Error("Failed to fetch user info");
        }
        const { data } = await response.json();
        setRequesters(data);
      } catch (error) {
        console.error("ユーザー情報の取得に失敗しました:", error);
      }
    };

    fetchUser();
    const handleFocus = () => {
      fetchUser();
    };

    window.addEventListener("focus", handleFocus);

    return () => {
      window.removeEventListener("focus", handleFocus);
    };
  }, []);

  const handleChange = (valueArray) => {
    const newValues = customerTypeChoices.reduce((acc, choice) => {
      acc[choice.id] = valueArray.includes(choice.id);
      return acc;
    }, {});
    setCustomerTypes(newValues);
  };

  useEffect(() => {
    if (data) {
      const type = {
        isCleanCus: data.isCleanCus,
        isInspectCus: data.isInspectCus,
        isConstructCus: data.isConstructCus,
        isGarbageCommitCus: data.isGarbageCommitCus,
        isGarbageAllowCus: data.isGarbageAllowCus,
        isWasteCus: data.isWasteCus,
      };
      setCustomerTypes(type);
    }
  }, [data]);

  const SmallTextInput = (props) => (
    <>
      {/* <div style={{display:"flex",verticalAlign:"middle"}}> */}
      {(props.title) ? (
        props.title === "備考" ? (
          <div style={{ backgroundColor: "#777", padding: "3px", margin: "10px 0 0", fontSize: "12px", color: "#fff", whiteSpace: "nowrap" }}>{props.title}</div>
        ) : (
          <div style={{ backgroundColor: "#777", padding: "3px", margin: "10px 0 5px", fontSize: "12px", color: "#fff", whiteSpace: "nowrap" }}>{props.title}</div>
        )
      ) : (
        <></>
      )}
      <TextInput
        helperText={false}
        label={false}
        {...props}
        sx={{
          '& .MuiInputBase-input': {
            padding: "5px",
            fontSize: '12px',
          },
          p: 0,
          mb: 0,
          backgroundColor: "#f2f2f2",
          ...(props.title === "備考" && { mt: 0 }),
        }}
      />
      {/* </div> */}
    </>
  );

  const SmallSelectInput = (props) => (
    <>
      {props.title && <div style={{ backgroundColor: "#777", padding: "3px", margin: "10px 0 5px", fontSize: "12px", color: "#fff", whiteSpace: "nowrap" }}>{props.title}</div>}
      <SelectInput
        helperText={false}
        {...props}
        sx={{
          '& .MuiInputBase-input': {
            fontSize: '12px',
          },
          '& .MuiInputLabel-root': {
            fontSize: '10px',
          },
          '& .MuiFormHelperText-root': {
            margin: 0,  // エラーメッセージ等の余白防止
          },
          '& .MuiSelect-select': {
            padding: "2px",
            fontSize: '12px',
          },
          mb: 0,
          p: 0,
          minWidth: 0,
          backgroundColor: "#f2f2f2"
        }}
      />
    </>
  );

  const SmallCheckBox = (props) => (
    <>
      {props.title && <div style={{ backgroundColor: "#777", padding: "3px", margin: "10px 0 0", fontSize: "12px", color: "#fff", whiteSpace: "nowrap" }}>{props.title}</div>}
      <CheckboxGroupInput
        {...props}
        helperText={false}
        sx={{
          borderBottom: "1px solid #aaa",
          backgroundColor: "#f2f2f2",
          mb: 0,
          mt: 0,
          '& .MuiFormGroup-root': {
            fontSize: '10px', // ✅ チェック項目のテキストサイズ

          },
          '& .MuiFormControlLabel-label': {
            fontSize: '12px', // ✅ 各ラベルの文字サイズ
          },
          '& .MuiInputLabel-root': {
            fontSize: '12px', // ✅ 上部のラベル「顧客タイプ」
          },
          '& .MuiSvgIcon-root': {
            fontSize: '16px', // ✅ チェックボックスの本体サイズを小さく
          },
          '& .MuiButtonBase-root': {
            background: "none",
            border: "none"
          }
        }}
      />
    </>
  );

  const CustomTopToolbar = () => (
    <Box sx={{ padding: 0, marginBottom: -2 }}>
      <TopToolbar>
        <Button
          label="顧客詳細表示"
          onClick={() => redirect(`/${resource}/show/${id}`)}
          startIcon={<VisibilityIcon />}
        />
      </TopToolbar>
    </Box>
  );

  return (
    <>
      <CustomTopToolbar />
      <Edit mutationMode="pessimistic"
        mutationOptions={{
          onSuccess: () => {
            notify('更新が完了しました', { type: 'success' });
            // refetch();
            // redirect('list', resource);
            window.history.back();
          }
        }}
      >
        <SimpleForm
          toolbar={<AlwaysEnabledToolbar />}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
            }
          }}
          sx={{ backgroundColor: "#f0f8ff" }}>
          <div style={{ display: "flex", width: "100%" }}>
            <div style={{ width: "50%", margin: "0 5px" }}>
              <div style={{ marginBottom: "15px" }}>
                <div style={{ backgroundColor: "#1976d2", padding: "3px", margin: "10px 0 5px", fontSize: "12px", color: "#fff", whiteSpace: "nowrap" }}>基本情報</div>
                {(data) && <div style={{ margin: "0 5px" }}><BaseInfo customerTypes={customerTypes} handleChange={handleChange} SmallTextInput={SmallTextInput} SmallSelectInput={SmallSelectInput} SmallCheckBox={SmallCheckBox} data={data} /></div>}
              </div>
            </div>
            <div style={{ width: "50%", margin: "0 5px" }}>
              {(customerTypes.isCleanCus || customerTypes.isConstructCus || customerTypes.isInspectCus) ? (
                <div style={{ marginBottom: "15px" }}>
                  <div style={{ backgroundColor: "#1976d2", padding: "3px", margin: "10px 0 5px", fontSize: "12px", color: "#fff", whiteSpace: "nowrap" }}>浄化槽情報</div>
                  <div style={{ margin: "0 5px" }}><TankInfo SmallSelectInput={SmallSelectInput} SmallTextInput={SmallTextInput} isCleanCus={customerTypes.isCleanCus} /></div>
                </div>
              ) : (
                <></>
              )}
              {(customerTypes.isCleanCus) ? (
                <div style={{ marginBottom: "15px" }}>
                  <div style={{ backgroundColor: "#1976d2", padding: "3px", margin: "10px 0 5px", fontSize: "12px", color: "#fff", whiteSpace: "nowrap" }}>清掃情報</div>
                  <div style={{ margin: "0 5px" }}><CleanInfo SmallSelectInput={SmallSelectInput} SmallTextInput={SmallTextInput} requesters={requesters} data={data} /></div>
                </div>
              ) : (
                <></>
              )}
              {(customerTypes.isInspectCus) ? (
                <div style={{ marginBottom: "15px" }}>
                  <div style={{ backgroundColor: "#1976d2", padding: "3px", margin: "10px 0 5px", fontSize: "12px", color: "#fff", whiteSpace: "nowrap" }}>点検情報</div>
                  <div style={{ margin: "0 5px" }}><InspectInfo SmallSelectInput={SmallSelectInput} SmallTextInput={SmallTextInput} SmallCheckBox={SmallCheckBox} data={data} /></div>
                </div>
              ) : (
                <></>
              )}
              {customerTypes.isGarbageAllowCus ? (
                <>
                  <div style={{ backgroundColor: "#1976d2", padding: "3px", margin: "10px 0 5px", fontSize: "12px", color: "#fff", whiteSpace: "nowrap" }}>ごみ収集許可情報</div>
                  <div style={{ margin: "0 5px" }}>
                    <SmallCheckBox
                      source="garbageCollectWeek"
                      label={false} title={<span style={{ fontSize: "12px", margin: "0", padding: "0" }}>ごみ収集の曜日</span>}
                      choices={checkWeekChoices}
                      format={(value) => (value ? value.split("、").map(v => v.trim()) : [])} parse={(value) => (Array.isArray(value) ? value.join("、") : "")}
                    />
                  </div>
                </>

              ) : (
                <></>
              )
              }
            </div>
          </div>

          {(customerTypes.isInspectCus || customerTypes.isGarbageAllowCus) ? (
            <>{(data) && <ContractInfo syncContractInfo={syncContractInfo} setSyncContractInfo={setSyncContractInfo} SmallTextInput={SmallTextInput} SmallSelectInput={SmallSelectInput} data={data} />}</>
          ) : (
            <></>
          )}
        </SimpleForm>
      </Edit>
    </>
  )

};

const ExPumpingTotalField = () => {
  const { setValue } = useFormContext();
  const amount = useWatch({ name: 'exPumpingAmount' });
  const pay = useWatch({ name: 'exPumpingPay' });

  useEffect(() => {
    if (!isNaN(amount) && !isNaN(pay)) {
      const total = Number(amount) * Number(pay);
      setValue('exPumpingAmountTotal', total);
    } else {
      setValue('exPumpingAmountTotal', '');
    }
  }, [amount, pay, setValue]);

  return null; // 表示せず自動計算に専念
};

const TankInfo = ({ SmallTextInput, SmallSelectInput, isCleanCus }) => {
  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
        <SmallTextInput source="processMethod" label={false} title="処理方式" style={{ marginRight: "10px" }} />
        <SmallSelectInput
          source="processCategory"
          label={false} title="処理区分"
          choices={[{ id: 1, name: "単独" }, { id: 2, name: "合併" }]}
          style={{ marginRight: "10px" }}
        />
        <SmallTextInput source="scale" label={false} title="規模" />
      </div>
      {/* {isCleanCus ? (
        <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
        <SmallTextInput source="exPumpingAmount" label={false} title="汲取量(㎥)" type="number" style={{ marginRight: "10px" }} />
        <SmallTextInput source="exPumpingPay" label={false} title="単価(円)" defaultValue={9000} 
        />
        <SmallTextInput source="exPumpingAmountTotal" label={false} title="金額(円)" style={{ marginRight: "10px" }} 
          disabled
          format={(value) => {
            if (value === null || value === undefined || isNaN(value)) return '';
            return Number(value).toLocaleString();
          }}
        />
        <ExPumpingTotalField/>
      </div>
      ) : (
        <></>
      )} */}
      <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
        <SmallTextInput source="tankMaker" label={false} title="浄化槽メーカー" style={{ marginRight: "10px" }} />
        <SmallTextInput source="tankType" label={false} title="浄化槽型式" />
      </div>
    </>

  )
}

const CleanInfo = ({ SmallTextInput, SmallSelectInput, requesters, data }) => {
  const { setValue, getValues } = useFormContext();
  useEffect(() => {
    const data = getValues("exPumpingPay");
    setValue("exPumpingPay", data ? data.toLocaleString() : "9,000");
  }, []);
  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
        <SmallTextInput source="exPumpingPay" label={false} title="清掃単価(円)" />
        {/* <ExPumpingTotalField/> */}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
        <SmallSelectInput
          source="requesterId"
          label={false} title="管理業者"
          choices={requesters.map(req => ({
            id: req.id,
            name: `${getNameAndCompanyNamewithAddress(req)}`
          }))}
          style={{ flex: 2 }}
        />
        <div style={{ flex: 1, margin: "auto 0", marginLeft: "10px" }}>
          <Link
            onClick={() => {
              const url = `${window.location.origin}/#/requesters/create`
              const features = 'width=1200,height=800,left=200,top=100,noopener,noreferrer';
              window.open(url, '_blank', features);
            }}
          >管理業者を新規追加</Link>
        </div>
      </div>
    </>

  )
}

const InspectInfo = ({ SmallTextInput, SmallSelectInput, SmallCheckBox, data }) => {
  const { setValue, getValues } = useFormContext();
  useEffect(() => {
    const data = getValues("contractAmount");
    setValue("contractAmount", data ? data.toLocaleString() : "");
  }, []);
  return (
    <>
      <SmallCheckBox
        source="checkMonth"
        label={false} title={<span style={{ fontSize: "12px", margin: "0", padding: "0" }}>点検月</span>}
        choices={checkMonthChoices}
        format={(value) => (value ? value.split("、").map(v => v.trim()) : [])} parse={(value) => (Array.isArray(value) ? value.join("、") : "")}
      />

      <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }} >
        <SmallSelectInput
          source="checkMonthNum"
          label={false} title="点検回数"
          choices={[...Array(12)].map((_, i) => ({
            id: (i + 1).toString(),
            name: `${i + 1}`,
          }))}
          style={{ flex: 1, marginRight: "10px" }}
          defaultValue={data.checkMonth ? (data.checkMonth.split("、")).length : 0}
        />
        <SmallSelectInput
          source="invoiceMonth"
          label={false} title="請求月"
          choices={checkMonthChoices}
          style={{ flex: 1, marginRight: "10px" }}
        />
        <SmallTextInput source="contractAmount" label={false} title="保守点検料(税抜き)" style={{ flex: 2 }}

        />
      </div>
    </>
  )
}

const BaseInfo = ({ customerTypes, handleChange, SmallCheckBox, SmallTextInput, SmallSelectInput, data, set }) => {
  const { setValue, getValues } = useFormContext();

  useEffect(() => {
    if (customerTypes) {
      setValue("customerTypes", customerTypes);
    }
  }, [customerTypes, setValue]);

  const [cityList, setCityList] = useState([]);
  const [selectPref, setSelectPref] = useState(null);
  const [selectCity, setSelectCity] = useState(null);
  const [selectTown, setSelectTown] = useState(null);
  const [townList, setTownList] = useState([]);
  const [bukkenLoad, setBukkenLoad] = useState(false);
  const [nameLoad, setNameLoad] = useState(false);
  const [postLoad, setPostLoad] = useState(false);
  const [addressLoad, setAddressLoad] = useState(false);

  useEffect(() => {
    setCity();
  }, [selectPref]);

  const setCity = async () => {
    if (selectPref) {
      const cityList = await handleCityListLoad(selectPref);
      setCityList(cityList);
    }
  }

  useEffect(() => {
    setTown();
  }, [selectCity]);

  const setTown = async () => {
    if (selectCity && selectPref) {
      const townList = await handleTownListLoad(selectPref, selectCity);
      setTownList(townList);
    }
  }

  useEffect(() => {
    if (data.address) {
      initAddress();
    }
  }, []);

  const initAddress = async () => {
    setSelectPref(data.address);
    setSelectCity(data.address2);
    setSelectTown(data.address3);
  }

  const validateCustomerType = (value) => {
    // 配列形式で渡ってくるなら length を、オブジェクト形式なら true が1つでもあるかで判定
    if (!value || (Array.isArray(value) && value.length === 0)) {
      return '1つ以上選択してください';
    }
    if (typeof value === 'object' && !Object.values(value).some(v => v)) {
      return '1つ以上選択してください';
    }
    return undefined;
  };

  return (
    <>
      <SmallCheckBox
        source="customerTypes"
        label={false} title={<span style={{ fontSize: "12px", margin: "0", padding: "0" }}>顧客タイプ</span>}
        validate={validateCustomerType}
        choices={customerTypeChoices}
        format={(value) =>
          value ? Object.keys(value).filter(key => value[key]) : []
        }
        parse={(valueArray) => {
          const newValues = customerTypeChoices.reduce((acc, choice) => {
            acc[choice.id] = valueArray.includes(choice.id);
            return acc;
          }, {});
          return newValues;
        }}
        defaultValue={customerTypes}
        onChange={handleChange}
      />
      <div style={{ display: "flex", width: "100%" }}>
        <SmallTextInput source="bukken" label={false} title="物件名" style={{ flex: 1, marginRight: "10px" }} />
        <SmallTextInput source="bukken_k" label={false} title="カナ" style={{ flex: 1, marginRight: "10px" }}
          validate={[validateKatakana]}
          placeholder="カタカナのみ可"
        />
        <div style={{ margin: "auto 0" }}>
          <Button onClick={() => {
            (async () => {
              try {
                setBukkenLoad(true)
                let str = getValues("bukken")
                if(str){
                  str
                  .replace(/^(株式会社|合同会社|有限会社)/, '')
                  .replace(/(株式会社|合同会社|有限会社)$/, '');
                  const kuroshiro = await initKuroshiro();
                  let kata = await kuroshiro.convert(str, { to: "katakana" });
                  kata = getRomaToKana(kata);
                  setValue("bukken_k",kata)
                }
              } catch (error) {
                console.log(error);
                alert("エラーが発生しました");
              }
              finally{
                setBukkenLoad(false);
              }
            })();
          }}
          >{bukkenLoad && <CircularProgress size={16} sx={{ mr: 1 }} />}フリガナ自動入力</Button>
        </div>
      </div>
      <div style={{ display: "flex", width: "100%" }}>
        <SmallTextInput source="surname" label={false} title="顧客名" style={{ flex: 1, marginRight: "10px" }} />
        <SmallTextInput source="surname_k" label={false} title="カナ" style={{ flex: 1, marginRight: "10px" }}
          validate={[validateKatakana]}
          placeholder="カタカナのみ可"
        />
        <div style={{ margin: "auto 0" }}>
          <Button onClick={() => {
            (async () => {
              try {
                setNameLoad(true)
                let str = getValues("surname")
                if(str){
                  str
                  .replace(/^(株式会社|合同会社|有限会社)/, '')
                  .replace(/(株式会社|合同会社|有限会社)$/, '');
                  const kuroshiro = await initKuroshiro();
                  let kata = await kuroshiro.convert(str, { to: "katakana" });
                  kata = getRomaToKana(kata);
                  setValue("surname_k",kata)
                }                
              } catch (error) {
                console.log(error);
                alert("エラーが発生しました");               
              }
              finally{
                setNameLoad(false)
              }

            })();
            
          }}>{nameLoad && <CircularProgress size={16} sx={{ mr: 1 }} />}フリガナ自動入力</Button>
        </div>
      </div>
      {/* <div style={{ display: "flex", width: "100%" }}>
        <SmallTextInput source="surname" label={false} title="姓" style={{ flex: 1, marginRight: "10px" }} />
        <SmallTextInput source="name" label={false} title="名" style={{ flex: 1 }} />
      </div>
      <div style={{ display: "flex", width: "100%" }}>
        <SmallTextInput source="surname_k" label={false} title="セイ(カナ)" style={{ flex: 1, marginRight: "10px" }}
            validate={[validateKatakana]}
            placeholder="カタカナのみ可"
        />
        <SmallTextInput source="name_k" label={false} title="メイ(カナ)" style={{ flex: 1 }} 
            validate={[validateKatakana]}
            placeholder="カタカナのみ可"
        />
      </div>
      <div style={{ display: "flex", width: "100%" }}>
        <SmallTextInput source="companyName" label={false} title="会社名" style={{ flex: 1, marginRight: "10px" }} />
        <SmallTextInput source="companyName_k" label={false} title="会社名(カナ)" style={{ flex: 1 }} 
            validate={[validateKatakana]}
            placeholder="カタカナのみ可"
        />
      </div> */}
      <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
        <SmallTextInput source="phone" label={false} title="電話番号１" style={{ marginRight: "10px" }} />
        <SmallTextInput source="telephone" label={false} title="電話番号２" />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
        <SmallTextInput source="fax" label={false} title="FAX" style={{ marginRight: "10px" }} />
        <SmallTextInput source="mail" label={false} title="メール" type="email" />
      </div>
      <div style={{ display: "flex", width: "100%" }}>
        <SmallTextInput source="postCode" label={false} title="郵便番号" style={{ flex: 1, marginRight: "10px" }} />
        <div style={{ margin: "auto 0" }}>
          <Button onClick={() => {
            setAddressLoad(true)
            handlePostCodeChange(getValues("postCode")).then((data)=>{
              if(data){
                const { address1, address2, address3 } = data;
                if(address1){
                  setSelectPref(address1);
                  setValue("address",address1);
                }
                if(address2){
                  setSelectCity(address2);
                  setValue("address2",address2);
                }
                if(address3){
                  setSelectTown(address3);
                  setValue("address3",address3);
                }
              }
            }).catch((e)=>{
              alert(`住所の取得に失敗しました:${e}`)
            }).finally(()=>{
              setAddressLoad(false);
            })
          }}
          >{addressLoad && <CircularProgress size={16} sx={{ mr: 1 }} />}郵便番号で自動入力</Button>
        </div>
      </div>
      <div style={{ display: "flex", width: "100%" }}>
        <SmallSelectInput source="address" label={false} title="住所"
          style={{ marginRight: "5px", flex: 1.5 }}
          choices={areaChoices}
          onChange={(e) => {
            const pref = areaChoices.find((item) => item.name === e.target.value);
            if (pref) {
              setSelectPref(pref.name)
            }
            else {
              setSelectPref(null)
            }
          }}
          optionValue="name"
          value={selectPref}
        />
        <SmallSelectInput source="address2" label={false} title={false}
          style={{ marginRight: "5px", flex: 1.5 }}
          choices={cityList}
          optionValue="name"
          onChange={(e) => {
            const city = cityList.find((item) => item.name === e.target.value);
            if (city) {
              setSelectCity(city.name)
            }
            else {
              setSelectCity(null)
            }
          }}
          value={selectCity}
        />
        <SmallSelectInput source="address3" label={false} title={false}
          style={{ marginRight: "5px", flex: 1.5 }}
          choices={townList}
          optionValue="name"
          onChange={(e) => {
            const town = townList.find((item) => item.name === e.target.value);
            if (town) {
              setSelectTown(town.name)
            }
            else {
              setSelectTown(null)
            }
          }}
          value={selectTown}
        />
      </div>
      <div style={{ display: "flex", width: "100%" }}>
        <SmallTextInput source="address4" label={false} title={null} style={{ marginRight: "5px", flex: 2 }} placeholder="番地" />
        <SmallTextInput source="address5" label={false} title={null} style={{ flex: 2 }} placeholder="建物名・部屋番号" />
        <div style={{ margin: "auto 0", flex: 1.5 }}>
          <Button onClick={() => {
              setPostLoad(true)
              handleAddressChange( getValues("address")+getValues("address2")+getValues("address3")+getValues("address4")).then((postCode)=>{
                if(postCode){
                  setValue("postCode",postCode);
                }
              }).catch((e)=>{
                alert(`郵便番号の取得に失敗しました:${e}`)
              }).finally(()=>{
                setPostLoad(false);
              })
            }}
          >{postLoad && <CircularProgress size={16} sx={{ mr: 1 }} />}住所で自動入力</Button>
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
        <SmallTextInput source="mapNumber" label={false} title="ゼンリン地図番号" type="number" style={{ marginRight: "10px", flex: 2 }} placeholder="数字3桁" />
        <SmallSelectInput source="mapNumber2" label={false}
          choices={Array.from({ length: 10 }, (_, i) => ({
            id: String.fromCharCode(65 + i),
            name: String.fromCharCode(65 + i),
          }))}
          style={{ marginRight: "10px", flex: 1 }}
        />
        <SmallSelectInput source="mapNumber3" label={false}
          choices={Array.from({ length: 5 }, (_, i) => ({
            id: String(i + 1),
            name: String(i + 1),
          }))}
          style={{ flex: 1 }}
        />
      </div>
      <SmallTextInput source="remarks" label={false} title="備考" multiline minRows={3} variant="standard" style={{ backgroundColor: "#f2f2f2" }} />
    </>
  );
}


const ContractInfo = ({ syncContractInfo, setSyncContractInfo, SmallTextInput, SmallSelectInput, data }) => {
  const { setValue, getValues } = useFormContext(); // ✅ `SimpleForm` 内で実行

  const [cityList, setCityList] = useState([]);
  const [selectPref, setSelectPref] = useState(null);
  const [selectCity, setSelectCity] = useState(null);
  const [selectTown, setSelectTown] = useState(null);
  const [townList, setTownList] = useState([]);
  const [postLoad, setPostLoad] = useState(false);
  const [addressLoad, setAddressLoad] = useState(false);

  const [cityList_o, setCityList_o] = useState([]);
  const [selectPref_o, setSelectPref_o] = useState(null);
  const [selectCity_o, setSelectCity_o] = useState(null);
  const [selectTown_o, setSelectTown_o] = useState(null);
  const [townList_o, setTownList_o] = useState([]);
  const [postLoad_o, setPostLoad_o] = useState(false);
  const [addressLoad_o, setAddressLoad_o] = useState(false);

  useEffect(() => {
    setCity();
  }, [selectPref]);

  const setCity = async () => {
    if (selectPref) {
      const cityList = await handleCityListLoad(selectPref);
      setCityList(cityList);
    }
  }

  useEffect(() => {
    setTown();
  }, [selectCity]);

  const setTown = async () => {
    if (selectCity && selectPref) {
      const townList = await handleTownListLoad(selectPref, selectCity);
      setTownList(townList);
    }
  }

  useEffect(() => {
    setCity_o()
  }, [selectPref_o]);

  const setCity_o = async () => {
    if (selectPref_o) {
      const cityList = await handleCityListLoad(selectPref_o);
      setCityList_o(cityList);
    }
  }

  useEffect(() => {
    setTown_o();
  }, [selectCity_o]);

  const setTown_o = async () => {
    if (selectPref_o && selectCity_o) {
      const townList = await handleTownListLoad(selectPref_o, selectCity_o);
      setTownList_o(townList)
    }
  }

  useEffect(() => {
    initAddress();
  }, []);

  const initAddress = async () => {
    setSelectPref(data.contract_address);
    setSelectCity(data.contract_address2);
    setSelectTown(data.contract_address3);
    setSelectPref_o(data.orner_address);
    setSelectCity_o(data.orner_address2);
    setSelectTown_o(data.orner_address3);
  }

  // const handleSyncToggle = async(event) => {
  //   const checked = event.target.checked;
  //   setSyncContractInfo(checked);

  //   if (checked) {
  //     await handleCityListLoad(getValues("address"));
  //     await handleTownListLoad(getValues("address"),getValues("address2"))
  //     setSelectPref(getValues("address"));
  //     setSelectCity(getValues("address2"));
  //     setSelectTown(getValues("address3"));
  //     setValue("contract_companyName", getValues("companyName"));
  //     setValue("contract_surname", getValues("surname"));
  //     setValue("contract_name", getValues("name"));
  //     setValue("contract_postCode", getValues("postCode"));
  //     setValue("contract_address", getValues("address"));
  //     setValue("contract_address2", getValues("address2"));
  //     setValue("contract_address3", getValues("address3"));
  //     setValue("contract_address4", getValues("address4"));
  //     setValue("contract_address5", getValues("address5"));
  //     setValue("contract_phone", getValues("phone"));
  //     setValue("contract_mail", getValues("mail"));
  //   } else {
  //     setValue("contract_companyName", "");
  //     setValue("contract_surname", "");
  //     setValue("contract_name", "");
  //     setValue("contract_postCode", "");
  //     setValue("contract_address", "");
  //     setValue("contract_address2", "");
  //     setValue("contract_address3", "");
  //     setValue("contract_address4", "");
  //     setValue("contract_address5", "");
  //     setValue("contract_phone", "");
  //     setValue("contract_mail", "");
  //   }
  // };

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
        <div style={{ width: "50%", margin: "0 5px" }}>
          <div style={{ backgroundColor: "#1976d2", padding: "3px", margin: "10px 0 5px", fontSize: "12px", color: "#fff", whiteSpace: "nowrap" }}>契約者情報</div>
          <div style={{ margin: "0 5px" }}>
            {/* <FormControlLabel
              control={<Switch checked={syncContractInfo} onChange={e => handleSyncToggle(e)} />}
              label={<span style={{ fontSize: "12px", margin: "0", padding: "0" }}>基本情報を契約者情報にコピーする</span>}
              sx={{
                m: 0,
                p: 0
              }}
            /> */}
            <div style={{ display: "flex", width: "100%" }}>
              <SmallTextInput source="contract_surname" label={false} title="顧客名" style={{ flex: 1, marginRight: "10px" }} />
              {/* <SmallTextInput source="contract_name" label={false} title="名" style={{ flex: 1 }} /> */}
            </div>
            {/* <div style={{ display: "flex", width: "100%" }}>
                        <SmallTextInput source="contract_companyName" label={false} title="会社名" style={{ flex: 1 }} />
                      </div> */}
            <div style={{ display: "flex", width: "100%" }}>
              <SmallTextInput source="contract_phone" label={false} title="電話番号１" style={{ flex: 1, marginRight: "10px" }} />
              <SmallTextInput source="contract_telephone" label={false} title="電話番号２" style={{ flex: 1, marginRight: "10px" }} />
              <SmallTextInput source="contract_fax" label={false} title="FAX" style={{ flex: 1 }} />
            </div>
            <div style={{ display: "flex", width: "100%" }}>
              <SmallTextInput source="contract_postCode" label={false} title="郵便番号" style={{ flex: 1, marginRight: "10px" }} />
              <div style={{ margin: "auto 0" }}>
                <Button onClick={() => {
                  setAddressLoad(true)
                  handlePostCodeChange(getValues("contract_postCode")).then((data)=>{
                    if(data){
                      const { address1, address2, address3 } = data;
                      if(address1){
                        setSelectPref(address1);
                        setValue("contract_address",address1);
                      }
                      if(address2){
                        setSelectCity(address2);
                        setValue("contract_address2",address2);
                      }
                      if(address3){
                        setSelectTown(address3);
                        setValue("contract_address3",address3);
                      }
                    }
                    }).catch((e)=>{
                      alert(`住所の取得に失敗しました:${e}`)
                    }).finally(()=>{
                      setAddressLoad(false);
                    })
                  }}
                >{addressLoad && <CircularProgress size={16} sx={{ mr: 1 }} />}郵便番号で自動入力</Button>
              </div>
            </div>
            <div style={{ display: "flex", width: "100%" }}>
              <SmallSelectInput source="contract_address" label={false} title="住所"
                style={{ marginRight: "5px", flex: 1.5 }}
                choices={areaChoices}
                onChange={(e) => {
                  const pref = areaChoices.find((item) => item.name === e.target.value);
                  if (pref) {
                    setSelectPref(pref.name)
                  }
                  else {
                    setSelectPref(null)
                  }
                }}
                optionValue="name"
                value={selectPref}
                defaultValue={selectPref}
              />
              <SmallSelectInput source="contract_address2" label={false} title={false}
                style={{ marginRight: "5px", flex: 1.5 }}
                choices={cityList}
                optionValue="name"
                onChange={(e) => {
                  const city = cityList.find((item) => item.name === e.target.value);
                  if (city) {
                    setSelectCity(city.name)
                  }
                  else {
                    setSelectCity(null)
                  }
                }}
                value={selectCity}
                defaultValue={selectCity}
              />
              <SmallSelectInput source="contract_address3" label={false} title={false}
                style={{ flex: 1.5 }}
                choices={townList}
                optionValue="name"
                onChange={(e) => {
                  const town = townList.find((item) => item.name === e.target.value);
                  if (town) {
                    setSelectTown(town.name)
                  }
                  else {
                    setSelectTown(null)
                  }
                }}
                value={selectTown}
              />
            </div>
            <div style={{ display: "flex", width: "100%" }}>
              <SmallTextInput source="contract_address4" label={false} title={null} style={{ marginRight: "5px", flex: 2 }} placeholder="番地"
              />
              <SmallTextInput source="contract_address5" label={false} title={null} style={{ marginRight: "10px", flex: 2 }} placeholder="建物名・部屋番号"
              />
              <div style={{ margin: "auto 0", flex: 1.5 }}>
                <Button onClick={() => {
                  setPostLoad(true)
                  handleAddressChange( getValues("contract_address")+getValues("contract_address2")+getValues("contract_address3")+getValues("contract_address4")).then((postCode)=>{
                    if(postCode){
                      setValue("contract_postCode",postCode);
                    }
                    }).catch((e)=>{
                      alert(`郵便番号の取得に失敗しました:${e}`)
                    }).finally(()=>{
                      setPostLoad(false);
                    })
                  }}
                >{postLoad && <CircularProgress size={16} sx={{ mr: 1 }} />}住所で自動入力</Button>
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
              <div style={{ display: "flex" }}>
                <div style={{ backgroundColor: "#777", padding: "3px", margin: "10px 0 5px", fontSize: "12px", color: "#fff", whiteSpace: "nowrap" }}>契約日</div>
                <DateInput
                  source="contractDate"
                  label={false} title="契約日"
                  style={{ marginRight: "10px", flex: 1 }}
                  helperText={false}
                  sx={{
                    '& input': {
                      padding: '5px 0',   // 入力エリアの高さを縮める
                      fontSize: '12px',        // 入力文字サイズを小さく
                    },
                  }}
                  inputProps={{ max: '9999-12-31' }}
                />
              </div>
              {/* <SmallTextInput source="contractAmount" label={false} title="契約金(税抜き)" style={{ flex: 1 }} /> */}
            </div>
            <SmallTextInput source="contract_remarks" label={false} title="備考" multiline minRows={3} variant="standard" style={{ backgroundColor: "#f2f2f2" }} />
            <div style={{ backgroundColor: "#777", padding: "3px", margin: "10px 0 0", fontSize: "12px", color: "#fff", whiteSpace: "nowrap" }}>契約書</div>
            <FileInput
              source="documentURL"
              label={false} title="契約書"
              accept="application/pdf, image/*"
              sx={{
                '& .RaFileInput-dropZone': {
                  padding: '0px',
                },
                '& .MuiTypography-root': {
                  fontSize: '10px',
                },
                mb: 0,
              }}
              style={{ flex: 5 }}
            >
              < FileField source="src" title="title" />
            </FileInput>

          </div>
        </div>
        <div style={{ width: "50%", margin: "0 5px" }}>
          <div style={{ backgroundColor: "#1976d2", padding: "3px", margin: "10px 0 5px", fontSize: "12px", color: "#fff", whiteSpace: "nowrap" }}>オーナー情報</div>
          <div style={{ margin: "0 5px" }}>
            <div style={{ display: "flex", width: "100%" }}>
              <SmallTextInput source="orner_surname" label={false} title="顧客名" style={{ flex: 1, marginRight: "10px" }} />
              {/* <SmallTextInput source="orner_name" label={false} title="名" style={{ flex: 1 }} /> */}
            </div>
            {/* <div style={{ display: "flex", width: "100%" }}>
                        <SmallTextInput source="orner_companyName" label={false} title="会社名" />
                      </div> */}
            <div style={{ display: "flex", width: "100%" }}>
              <SmallTextInput source="orner_phone" label={false} title="電話番号１" style={{ flex: 1.5, marginRight: "10px" }} />
              <SmallTextInput source="orner_telephone" label={false} title="電話番号２" style={{ flex: 1.5, marginRight: "10px" }} />
              <SmallTextInput source="orner_fax" label={false} title="FAX" style={{ flex: 1.5 }} />
            </div>
            <div style={{ display: "flex", width: "100%" }}>
              <SmallTextInput source="orner_postCode" label={false} title="郵便番号" style={{ flex: 1, marginRight: "10px" }} />
              <div style={{ margin: "auto 0" }}>
                <Button onClick={() => {
                  setAddressLoad_o(true)
                  handlePostCodeChange(getValues("orner_postCode")).then((data)=>{
                    if(data){
                      const { address1, address2, address3 } = data;
                      if(address1){
                        setSelectPref_o(address1);
                        setValue("orner_address",address1);
                      }
                      if(address2){
                        setSelectCity_o(address2);
                        setValue("orner_address2",address2);
                      }
                      if(address3){
                        setSelectTown_o(address3);
                        setValue("orner_address3",address3);
                      }
                    }
                  }).catch((e)=>{
                    alert(`住所の取得に失敗しました:${e}`)
                  }).finally(()=>{
                    setAddressLoad_o(false);
                  })
                }}
                >{addressLoad_o && <CircularProgress size={16} sx={{ mr: 1 }} />}郵便番号で自動入力</Button>
              </div>
            </div>
            <div style={{ display: "flex", width: "100%" }}>
              <SmallSelectInput source="orner_address" label={false} title="住所"
                style={{ marginRight: "5px", flex: 1.5 }}
                choices={areaChoices}
                onChange={(e) => {
                  const pref = areaChoices.find((item) => item.name === e.target.value);
                  if (pref) {
                    setSelectPref_o(pref.name)
                  }
                  else {
                    setSelectPref_o(null)
                  }
                }}
                optionValue="name"
                value={selectPref_o}
                defaultValue={selectPref_o}
              />
              <SmallSelectInput source="orner_address2" label={false} title={false}
                style={{ marginRight: "5px", flex: 1.5 }}
                choices={cityList_o}
                optionValue="name"
                onChange={(e) => {
                  const city = cityList_o.find((item) => item.name === e.target.value);
                  if (city) {
                    setSelectCity_o(city.name)
                  }
                  else {
                    setSelectCity_o(null)
                  }
                }}
                value={selectCity_o}
                defaultValue={selectCity_o}
              />
              <SmallSelectInput source="orner_address3" label={false} title={false}
                style={{ flex: 1.5 }}
                choices={townList_o}
                optionValue="name"
                onChange={(e) => {
                  const town = townList_o.find((item) => item.name === e.target.value);
                  if (town) {
                    setSelectTown_o(town.name)
                  }
                  else {
                    setSelectTown_o(null)
                  }
                }}
                value={selectTown_o}
              />
            </div>
            <div style={{ display: "flex", width: "100%" }}>
              <SmallTextInput source="orner_address4" label={false} title={null} style={{ marginRight: "5px", flex: 2 }} placeholder="番地"
              />
              <SmallTextInput source="orner_address5" label={false} title={null} style={{ marginRight: "10px", flex: 2 }} placeholder="建物名・部屋番号"
              />
              <div style={{ margin: "auto 0", flex: 1.5 }}>
                <Button onClick={() => {
                  setPostLoad_o(true)
                  handleAddressChange( getValues("orner_address")+getValues("orner_address2")+getValues("orner_address3")+getValues("orner_address4")).then((postCode)=>{
                    if(postCode){
                      setValue("orner_postCode",postCode);
                    }
                    }).catch((e)=>{
                      alert(`郵便番号の取得に失敗しました:${e}`)
                    }).finally(()=>{
                      setPostLoad_o(false);
                    })
                  }}
                  >{postLoad_o && <CircularProgress size={16} sx={{ mr: 1 }} />}住所で自動入力</Button>
              </div>
            </div>
            <SmallTextInput source="orner_remarks" label={false} title="備考" multiline minRows={3} variant="standard" style={{ backgroundColor: "#f2f2f2" }} />
          </div>
        </div>
      </div>
    </>
  );
};

export default CustomerEdit;
